import { FC, useContext, useMemo, useState } from 'react';

import {
  Button,
  Checkbox,
  ContextMenu,
  Table,
  Tabs,
  useContextMenu,
  ITabOption,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column } from 'react-table';

import { LayoutContext } from 'components/Layout';
import { LocalizationContext } from 'localization';
import { TTask } from 'types';

import { ITasksBlockProps } from './interfaces';
import { TaskManager } from './TaskManager';
import s from './TasksBlock.module.scss';
import { useContextMenuItems } from './useContextMenuItems';
import { TMode, useTasks } from './useTasks';
import { formatFIO } from './utils';

export const TasksBlock: FC<ITasksBlockProps> = ({ permissions }) => {
  const { t } = useContext(LocalizationContext);
  const layoutElement = useContext(LayoutContext);

  const [isTaskManagerOpen, setIsTaskManagerOpen] = useState<boolean>(false);
  const [taskForEdit, setTaskForEdit] = useState<TTask | null>(null);

  const { tasks, setIsTaskCompleted, setMode, mode, loading } =
    useTasks(permissions);

  const TABS: Array<ITabOption<TMode>> = [
    { value: 'ACTIVE', title: t('tasks.tabs.active') },
    { value: 'FINISHED', title: t('tasks.tabs.completed') },
  ];

  const TASKS_COLUMNS = useMemo<Array<Column<TTask>>>(
    () => [
      {
        accessor: 'title',
        Header: t('task'),
        Cell: ({
          value,
          row: {
            original: { id, finished_at },
          },
        }) => (
          <span className={s['task-title']}>
            <Checkbox
              value={Boolean(finished_at)}
              onChange={() => setIsTaskCompleted(id, !finished_at)}
              disabled={!permissions?.has('worker_task_complete')}
            />
            <span className={s.title}>{value}</span>
          </span>
        ),
      },
      {
        id: 'period',
        accessor: ({ deadline_at }) => {
          if (deadline_at) {
            return dayjs(deadline_at).valueOf();
          }

          return null;
        },
        Header: t('task.period'),
        Cell: ({ value }: { value: number }) =>
          dayjs(value).format('D MMM YYYY'),
        width: 178,
        disableResizing: true,
      },
      {
        id: 'executor',
        accessor: ({ assigned_to }) =>
          assigned_to ? formatFIO(assigned_to) : undefined,
        Header: t('task.executor'),
        Cell: ({ value }: { value: string }) => (
          <span className={s['executor']}>{value}</span>
        ),
        width: 170,
        disableResizing: true,
      },
    ],
    [t]
  );

  const {
    contextMenuData,
    onContextMenu,
    closeContextMenu,
    cursorPosition,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
  } = useContextMenu<TTask>();

  const items = useContextMenuItems((task) => {
    setTaskForEdit(task);
    setIsTaskManagerOpen(true);
  });

  return (
    <div className={s.container}>
      {isTaskManagerOpen && (
        <TaskManager
          task={taskForEdit}
          onClose={() => {
            setTaskForEdit(null);
            setIsTaskManagerOpen(false);
          }}
        />
      )}
      {contextMenuData && cursorPosition && (
        <ContextMenu
          layoutElement={layoutElement}
          data={contextMenuData}
          items={items}
          closeContextMenu={closeContextMenu}
          position={cursorPosition}
        />
      )}
      <Table
        headerBlock={
          <div className={s.header}>
            <Tabs
              options={TABS}
              value={mode}
              onChange={setMode}
              className={s.tabs}
            />
            {permissions?.has('worker_task_manage') && (
              <Button
                variant="outline"
                size="s"
                className={s['add-task']}
                onClick={() => setIsTaskManagerOpen(true)}
                iconName="CirclePlusIcon"
              >
                {t('tasks.add')}
              </Button>
            )}
          </div>
        }
        emptyDataPlaceholder={t(
          mode === 'ACTIVE'
            ? 'tasks.placeholder.active'
            : 'tasks.placeholder.completed'
        )}
        data={tasks}
        columns={TASKS_COLUMNS}
        isLoading={loading}
        onRowDoubleClick={(event, data) => {
          if (permissions?.has('worker_task_manage')) {
            setIsTaskManagerOpen(true);
            setTaskForEdit(data);
          }
        }}
        onContextMenu={(e, data) => {
          if (permissions?.has('worker_task_manage')) {
            onContextMenu(e, data);
          }
        }}
        onTouchStart={(event, data) => {
          if (permissions?.has('worker_task_manage')) {
            onTouchStart(event, data);
          }
        }}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={s.table}
      />
    </div>
  );
};
